import { AuthenticationResult } from '@azure/msal-browser';
import Axios, { AxiosRequestConfig } from 'axios';
import msalInstance from './MsalInstance';

export const MSAL_SCOPES = {
    MMG: `https://${process.env.REACT_APP_MSAL_TENANT_NAME}.onmicrosoft.com/${process.env.REACT_APP_MSAL_APP_ID_URI}/read`,
    OPENID: 'openid'
};

export const loginRequest = {
    scopes: [MSAL_SCOPES.MMG]
};

export const MSAL_LOGOUT_URL = `https://login.microsoftonline.com/common/oauth2/logout`;

export const forgotPasswordRequest = {
    authority: `https://login.onevirtualmarketplace.com/${process.env.REACT_APP_MSAL_TENANT_NAME}.onmicrosoft.com/${process.env.REACT_APP_MSAL_FORGET_PASSWORD_POLICY}`,
    scopes: [
        `https://${process.env.REACT_APP_MSAL_TENANT_NAME}.onmicrosoft.com/mmg-api/read`
    ]
};

export const msalRequest = {
    scopes: [MSAL_SCOPES.MMG],
    extraQueryParameters: {}
};

// Simple debug method for msal debugging, easily clean up console when you don't want to see 100 msal messages
export const debugMSAL = (error: any) => {
    let showLogs = false;

    if (showLogs) {
        console.error('[debugMSAL]: ', error);
    }
};

const delay = (t: number) => new Promise(resolve => setTimeout(resolve, t));

export const acquireToken = async (): Promise<AuthenticationResult | null> => {
    if (!msalInstance.msalApp) {
        console.error('[acquireToken]: MSAL instance not initialized');
        return null;
    }

    try {
        // Step 1: Handle the redirect and get the response (usually contains ID token only)
        const redirectResponse = await msalInstance.msalApp.handleRedirectPromise();

        if (redirectResponse) {
            // Step 2: Set the active account
            msalInstance.msalApp.setActiveAccount(redirectResponse.account);
        }

        // Step 3: Use the active account to acquire token silently
        const account = msalInstance.msalApp.getActiveAccount();
        if (!account) {
            console.warn('No active account found, redirecting to login');
            await msalInstance.msalApp.loginRedirect({
                scopes: [MSAL_SCOPES.MMG]
            });
            return null;
        }

        const silentRequest = {
            scopes: [MSAL_SCOPES.MMG],
            account
        };

        try {
            const silentToken = await msalInstance.msalApp.acquireTokenSilent(
                silentRequest
            );
            return silentToken;
        } catch (silentError) {
            console.error(
                '[acquireToken]: Silent token acquisition failed, redirecting to login',
                silentError
            );
            await msalInstance.msalApp.loginRedirect({
                scopes: [MSAL_SCOPES.MMG]
            });
            return null;
        }
    } catch (error) {
        console.error('[acquireToken]: error acquiring token', error);

        // If we're already in an interaction, don't try to redirect again
        if (
            error &&
            typeof error === 'object' &&
            'errorCode' in error &&
            typeof error.errorCode === 'string' &&
            error.errorCode.includes('interaction_in_progress')
        ) {
            console.log(
                '[acquireToken]: Interaction already in progress, waiting...'
            );
            await delay(2000);
            return acquireToken();
        }

        // Only redirect if we're not already in an interaction
        await msalInstance.msalApp.loginRedirect({
            scopes: [MSAL_SCOPES.MMG]
        });
        return null;
    }
};

/*
 * check the token before every API call.
 * goal is to redirect to login when needed and avoid displaying errors unless it fails to redirect for some reason.
 * If it is invalid it will try again.  If it is still invalid, redirect to the login page
 * if it is currently redirecting to the login page (logininprogress()) then it will wait
 *
 */

export const getMSALAccounts = () => {
    return msalInstance.msalApp.getAllAccounts();
};

export const msalFetch = async (
    url: string,
    options: AxiosRequestConfig,
    isRetry = false
): Promise<any> => {
    const delayTime = msalInstance.msalApp === undefined ? 250 : 0;
    await delay(delayTime);

    try {
        const authResponse = await acquireToken();

        if (!authResponse || !authResponse.accessToken) {
            console.error('[msalFetch]: Invalid auth response', authResponse);

            // If we're retrying an offline request, we need to redirect to login
            if (isRetry) {
                console.log(
                    '[msalFetch]: Offline request retry failed, redirecting to login'
                );
                await msalInstance.msalApp.loginRedirect({
                    scopes: [MSAL_SCOPES.MMG]
                });
            }

            throw new Error(
                'Failed to retrieve access token. User may not be signed in or token retrieval failed.'
            );
        }

        // Ensure we have the full payload for the request
        const fullOptions: AxiosRequestConfig = {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${authResponse.accessToken}`,
                // dev-only: the API treats this header (Development environment
                // only) as "no Users row" so /signup is testable while signed in
                ...(process.env.NODE_ENV === 'development' &&
                localStorage.getItem('signupTestMode') === '1'
                    ? { 'X-Signup-Test': '1' }
                    : {})
            }
        };

        // If this is a retry of an offline request, ensure we have the data
        if (isRetry && !fullOptions.data) {
            console.error('[msalFetch]: Missing data in offline request retry');
            throw new Error('Missing request data in offline retry');
        }

        return await Axios(url, fullOptions);
    } catch (error) {
        // A cancelled request is intentional (a newer request superseded it),
        // not a failure — keep it out of console.error, but leave a quiet
        // (debug-gated) trace, and rethrow at once so we skip the generic 2s
        // backoff below and callers' cleanup runs immediately.
        if (Axios.isCancel(error)) {
            debugMSAL(`msalFetch cancelled (${url}): ${error}`);
            throw error;
        }
        console.error(`[msalFetch (${url})]`, { error, options });
        debugMSAL(`msalFetch: ${error}`);

        // If we're already in an interaction, wait and retry
        if (
            error &&
            typeof error === 'object' &&
            'errorCode' in error &&
            typeof error.errorCode === 'string' &&
            error.errorCode.includes('interaction_in_progress')
        ) {
            console.log(
                '[msalFetch]: Interaction already in progress, waiting...'
            );
            await delay(2000);
            return msalFetch(url, options, true);
        }

        if (isRetry) {
            throw error;
        }

        await delay(2000);
        throw error;
    }
};
